import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Send, Plus, Trash2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
         Calculator, AlertCircle, CheckCircle2, Store, Mail, X, UserX, AlertTriangle, MessageSquare, DollarSign, Upload, Paperclip, FileText, Image } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { usePermissions } from "../lib/PermissionsContext";
import { urlFichier } from '@/lib/fichiers';
import { TPS_RATE, TVQ_RATE, calculerLigne, calculerSemaine } from '@/lib/lmdsCalcul';
import { DetailDeductionsDialog } from '@/components/lmds/DetailDeductionsDialog';
import { PerformanceOnglet } from '@/components/lmds/PerformanceOnglet';
import { MesFacturesOnglet } from '@/components/lmds/MesFacturesOnglet';
import { RechercheClientGlobale } from '@/components/lmds/RechercheClientGlobale';

const API = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const TPS = TPS_RATE;  // 🧾 Installateurs F1 : taux centralisés dans lib/lmdsCalcul.js
const TVQ = TVQ_RATE;
const MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];

// ========== DATE UTILS ==========
function getWeekDates(year, week) {
  const jan1 = new Date(year, 0, 1);
  const daysToSunday = (7 - jan1.getDay()) % 7;
  const firstSunday = new Date(year, 0, 1 + daysToSunday);
  const start = new Date(firstSunday);
  start.setDate(start.getDate() + (week - 1) * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { start, end };
}

function formatShort(d) {
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()].slice(0,3)}.`;
}

function formatFull(d) {
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}

function dateToISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getCurrentWeek(year) {
  const today = new Date();
  const jan1 = new Date(year, 0, 1);
  const daysToSunday = (7 - jan1.getDay()) % 7;
  const firstSunday = new Date(year, 0, 1 + daysToSunday);
  const diff = Math.floor((today - firstSunday) / (7 * 24 * 60 * 60 * 1000));
  return Math.max(1, Math.min(52, diff + 1));
}

// ========== CALCULATION ==========
const r2 = (v) => Math.round(v * 100) / 100;  // arrondi local (extras/SC), les totaux viennent de lib/lmdsCalcul.js
// 🧾 Installateurs Phase 1 (F1, décision (a)) : le calcul dupliqué a été remplacé par le module unique `lib/lmdsCalcul.js`
// (strictement équivalent au backend `services/lmds_calcul_semaine.py`, goldens partagés). L'affichage en direct rejoint
// les valeurs réellement sauvegardées : tip extra cash compté dans les extras ; SC/km « payés cette semaine » ajoutés au CA brut.
function calcLigne(l, taux) {
  const { _L, _estPaye, ...reste } = calculerLigne(l, taux);
  return reste;
}

function calcTotaux(lignes, taux, deductionsAuto = 0) {
  const r = calculerSemaine({ lignes, total_deductions_auto: deductionsAuto }, taux);
  return {
    total_service_calls: r.total_service_calls, total_km: r.total_km, total_tip: r.total_tip, total_deductions: r.total_deductions,
    total_deductions_auto: r.total_deductions_auto,
    total_contrats: r.total_contrats, total_extra_installateur: r.total_extra_installateur, total_installateur_brut: r.total_installateur_brut,
    total_installateur_net: r.total_installateur_net, tps_total: r.tps_total, tvq_total: r.tvq_total, total_avec_taxes: r.total_avec_taxes,
  };
}

function fmt(v) { return new Intl.NumberFormat("fr-CA",{style:"currency",currency:"CAD"}).format(v||0); }

function newLigne() {
  return { id: crypto.randomUUID(), nom_client:"", date_rdv:"", si:"", type_rdv:"installation",
           client_propre:true, service_call:0, km_montant:0,
           deductions:0, description:"", total:0,
           total_extra_installateur:0, total_installateur:0, tps:0, tvq:0, total_avec_taxes:0,
           delegue_a_id:"", delegue_a_nom:"", est_delegation:false,
           delegation_depuis_id:"", delegation_depuis_nom:"",
           non_payee:false, non_payee_resolue:false, sc_paye_cette_semaine:false, sc_decision_prise:false };
}

// Dropdown with preset values + custom option
function PresetSelect({ value, onChange, presets, placeholder = "0,00", className = "" }) {
  const isPreset = presets.some(p => p.value === value);
  const [custom, setCustom] = useState(!isPreset && value > 0);
  return (
    <div className={`flex items-center gap-0.5 ${className}`}>
      <select
        value={custom ? "__custom__" : value}
        onChange={e => {
          if (e.target.value === "__custom__") { setCustom(true); }
          else { setCustom(false); onChange(parseFloat(e.target.value)); }
        }}
        className="text-xs bg-transparent border-0 focus:outline-none py-0 max-w-[5rem] cursor-pointer"
      >
        {presets.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        <option value="__custom__">Autre...</option>
      </select>
      {custom && (
        <input type="number" step="0.01" min="0" value={value}
          onChange={e => onChange(parseFloat(e.target.value)||0)}
          className="w-16 text-xs border-0 bg-transparent focus:outline-none text-right p-0" />
      )}
    </div>
  );
}

const SC_PRESETS = [
  { value: 0, label: "0,00" },
  { value: 32.5, label: "32,50 (1er)" },
  { value: 50, label: "50,00 (2e)" },
  { value: 66, label: "66,00 (3e+)" },
];
const KM_PRESETS = [
  { value: 0, label: "0,00" },
  { value: 25, label: "25,00" },
  { value: 50, label: "50,00" },
];

function NumInput({ value, onChange, className="" }) {
  return (
    <input
      type="number" step="0.01" min="0"
      value={value === 0 ? "" : value}
      onChange={e => onChange(parseFloat(e.target.value)||0)}
      placeholder="0"
      className={`w-full border-0 bg-transparent text-right text-xs px-1 py-0.5 focus:outline-none focus:bg-blue-50 rounded ${className}`}
    />
  );
}

function TextInput({ value, onChange, placeholder="", className="" }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full border-0 bg-transparent text-xs px-1 py-0.5 focus:outline-none focus:bg-blue-50 rounded ${className}`}
    />
  );
}

// ========== WEEK GRID ==========
function WeekGrid({ year, selectedWeek, semaineMeta, onSelect }) {
  const weeks = Array.from({length:52}, (_,i) => i+1);
  const metaMap = {};
  semaineMeta.forEach(m => { metaMap[m.semaine] = m; });

  return (
    <div className="grid grid-cols-13 gap-0.5" style={{gridTemplateColumns:"repeat(13,1fr)"}}>
      {weeks.map(w => {
        const { start } = getWeekDates(year, w);
        const meta = metaMap[w];
        const isSelected = w === selectedWeek;
        const hasMeta = !!meta;
        const isSent = meta?.rapport_envoye;
        return (
          <button
            key={w}
            onClick={() => onSelect(w)}
            data-testid={`week-btn-${w}`}
            title={`Semaine ${w} — ${formatShort(start)}`}
            className={`
              relative text-xs py-1.5 rounded transition-all font-medium
              ${isSelected ? "bg-[#1a237e] text-white shadow" : ""}
              ${!isSelected && isSent ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : ""}
              ${!isSelected && hasMeta && !isSent ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : ""}
              ${!isSelected && !hasMeta ? "bg-slate-100 text-slate-500 hover:bg-slate-200" : ""}
            `}
          >
            {w}
          </button>
        );
      })}
    </div>
  );
}

// ========== RAPPORT TABLE ==========
function RapportTable({ lignes, taux, onChange, onAddRow, onDeleteRow, onMoveRow, allInstallers = [], currentInstallerId = "", selectedRowId, setSelectedRowId, isInstallateur = false, deductionsAuto = 0, nbDeductions = 0, onDetailDeductions, ligneSurlignee = null }) {
  const totaux = calcTotaux(lignes, taux, deductionsAuto);
  const dedTotalHT = totaux.total_deductions + totaux.total_deductions_auto;
  const [delegateRowId, setDelegateRowId] = useState(null);
  const [scSuggestion, setScSuggestion] = useState({});

  function updateLigne(idx, field, val) {
    const updated = lignes.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, [field]: val };
      return calcLigne(next, taux);
    });
    onChange(updated);

    // Auto-detect service call amount when client name changes and SC > 0
    if (field === "nom_client" && val && val.trim().length > 2) {
      fetchServiceCallSuggestion(val.trim(), idx);
    }
  }

  function updateLigneMulti(idx, fields) {
    const updated = lignes.map((l, i) => {
      if (i !== idx) return l;
      return calcLigne({ ...l, ...fields }, taux);
    });
    onChange(updated);
  }

  async function fetchServiceCallSuggestion(clientName, idx) {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await axios.get(`${API_URL}/api/lmds/service-call-count/${encodeURIComponent(clientName)}`);
      if (res.status === 200) {
        const data = res.data;
        if (data.count > 0) {
          setScSuggestion(prev => ({ ...prev, [idx]: data }));
        } else {
          setScSuggestion(prev => { const n = {...prev}; delete n[idx]; return n; });
        }
      }
    } catch (e) { /* ignore */ }
  }

  function applySuggestion(idx, montant) {
    const updated = lignes.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, service_call: montant };
      return calcLigne(next, taux);
    });
    onChange(updated);
    setScSuggestion(prev => { const n = {...prev}; delete n[idx]; return n; });
  }

  return (
    <div className="space-y-5 max-w-full overflow-x-auto">
      <table className={`w-full border-collapse text-xs ${isInstallateur ? 'pointer-events-none opacity-90' : ''}`} style={{minWidth:"1150px"}}>
        <thead>
          <tr className="bg-[#1a237e] text-white">
            <th className="px-2 py-2 text-left font-medium w-32">Nom Client</th>
            <th className="px-2 py-2 text-center font-medium w-24">Date</th>
            <th className="px-2 py-2 text-center font-medium w-20">SI</th>
            <th className="px-2 py-2 text-right font-medium w-32 bg-[#c62828]">Service Call</th>
            <th className="px-2 py-2 text-right font-medium w-20">Km ($)</th>
            <th className="px-2 py-2 text-right font-medium w-16 text-red-200">Déd. ($)</th>
            <th className="px-2 py-2 text-left font-medium">Description</th>
            <th className="px-2 py-2 text-right font-medium w-20">Total ($)</th>
            <th className="px-2 py-2 text-right font-medium w-20 bg-red-900">Extras Inst.</th>
            <th className="px-2 py-2 text-right font-medium w-20 bg-red-900">Total Inst.</th>
            <th className="px-2 py-2 text-right font-medium w-14">TPS</th>
            <th className="px-2 py-2 text-right font-medium w-14">TVQ</th>
            <th className="px-2 py-2 text-right font-medium w-16 bg-red-900">Avec taxes</th>
            <th className="w-16 px-1 bg-[#1a237e]"></th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, idx) => {
            const isDelegated = !!l.delegue_a_id;
            const isFromDelegate = !!l.est_delegation;
            const isNonPayee = !!l.non_payee && !l.non_payee_resolue;
            const isDelegatedSC = isFromDelegate && (parseFloat(l.service_call) > 0 || parseFloat(l.km_montant) > 0);
            const isEnAttente = isDelegatedSC && !l.sc_paye_cette_semaine && !l.non_payee && !l.sc_decision_prise;
            const isEmpty = !l.nom_client && !l.total && !l.service_call && !l.total_extra_installateur && !l.deductions;
            return (
              <tr key={l.id}
                id={`ligne-${l.id}`}
                data-testid={`ligne-rapport-${l.id}`}
                data-surlignee={ligneSurlignee === l.id ? "true" : undefined}
                onClick={() => setSelectedRowId(selectedRowId === l.id ? null : l.id)}
                className={`border-b transition-colors cursor-pointer
                ${ligneSurlignee === l.id ? "!bg-yellow-100 ring-2 ring-yellow-400" : ""}
                ${selectedRowId === l.id ? "!bg-gray-300" : ""}
                ${isDelegated && selectedRowId !== l.id ? "bg-slate-100 opacity-60 border-slate-100" : ""}
                ${isFromDelegate && !isEnAttente && !isNonPayee && selectedRowId !== l.id ? "bg-blue-50 border-slate-100" : ""}
                ${isEnAttente && selectedRowId !== l.id ? "bg-amber-50 border-amber-200" : ""}
                ${isNonPayee && selectedRowId !== l.id ? "bg-red-100 border-red-300" : "border-slate-100"}
                ${!isDelegated && !isFromDelegate && !isNonPayee && l.deductions > 0 && !l.nom_client && selectedRowId !== l.id ? "bg-amber-50" : ""}
                ${isEmpty && !isDelegated && !isFromDelegate ? "opacity-40" : ""}
                hover:brightness-95`}>
                <td className="px-1 py-0.5">
                  {isDelegated
                    ? <span className="line-through text-slate-400 text-xs pl-1">{l.nom_client || "—"}</span>
                    : isNonPayee
                    ? <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
                        <TextInput value={l.nom_client} onChange={v => updateLigne(idx,"nom_client",v)} placeholder="Nom client..." className="text-red-700 font-semibold" />
                      </div>
                    : <TextInput value={l.nom_client} onChange={v => updateLigne(idx,"nom_client",v)} placeholder="Nom client..." />
                  }
                </td>
                <td className="px-1 py-0.5">
                  <input type="date" value={l.date_rdv} onChange={e => updateLigne(idx,"date_rdv",e.target.value)}
                    disabled={isDelegated}
                    className="w-full text-xs border-0 bg-transparent px-1 py-0.5 focus:outline-none focus:bg-blue-50 rounded disabled:text-slate-400" />
                </td>
                <td className="px-1 py-0.5">
                  {isDelegated
                    ? <span className="text-slate-400 text-xs pl-1">{l.si || "—"}</span>
                    : <TextInput value={l.si} onChange={v => updateLigne(idx,"si",v)} placeholder="SI..." />
                  }
                </td>
                <td className={`px-1 py-0.5 ${isNonPayee ? "bg-red-50" : ""}`}>
                  {isDelegated
                    ? <span className="text-xs text-slate-400 pl-1">{l.service_call || "—"}</span>
                    : <div className="relative">
                        <PresetSelect value={l.service_call} onChange={v => updateLigne(idx,"service_call",v)} presets={SC_PRESETS} />
                        {scSuggestion[idx] && (
                          <button
                            onClick={(e) => { e.stopPropagation(); applySuggestion(idx, scSuggestion[idx].montant_suggere); }}
                            className="absolute -top-5 left-0 z-10 bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded shadow-md whitespace-nowrap hover:bg-blue-700 transition-colors"
                            title={scSuggestion[idx].historique}
                            data-testid={`sc-suggestion-${idx}`}
                          >
                            {scSuggestion[idx].rang}e SC = {scSuggestion[idx].montant_suggere}$
                          </button>
                        )}
                      </div>
                  }
                </td>
                <td className="px-1 py-0.5">
                  {isDelegated
                    ? <span className="text-xs text-slate-400">{l.km_montant || "—"}</span>
                    : <PresetSelect value={l.km_montant} onChange={v => updateLigne(idx,"km_montant",v)} presets={KM_PRESETS} />
                  }
                </td>
                <td className="px-1 py-0.5">
                  {isDelegated
                    ? <span className="text-xs text-slate-400">—</span>
                    : <NumInput value={l.deductions} onChange={v => updateLigne(idx,"deductions",v)} className="text-red-600" />
                  }
                </td>
                <td className="px-1 py-0.5">
                  {isDelegated
                    ? <span className="text-xs text-slate-500 italic pl-1">→ {l.delegue_a_nom}</span>
                    : isFromDelegate
                    ? <span className="text-xs text-blue-600 italic pl-1 flex items-center gap-1">
                        <span>←</span> {l.delegation_depuis_nom}
                        {l.description && <span className="text-slate-500 ml-1">· {l.description}</span>}
                      </span>
                    : <TextInput value={l.description} onChange={v => updateLigne(idx,"description",v)} placeholder="Description..." />
                  }
                </td>
                <td className="px-1 py-0.5">
                  {isDelegated
                    ? <span className="text-xs text-slate-400 text-right block">{l.total || "—"}</span>
                    : <NumInput value={l.total} onChange={v => updateLigne(idx,"total",v)} />
                  }
                </td>
                <td className={`px-1 py-0.5 text-right font-medium ${isNonPayee ? "bg-red-50 text-red-700" : ""}`}>{l.total_extra_installateur > 0 ? l.total_extra_installateur.toFixed(2) : ""}</td>
                <td className={`px-1 py-0.5 text-right font-medium ${isNonPayee ? "bg-red-50 text-red-700" : ""}`}>{l.total_installateur > 0 ? l.total_installateur.toFixed(2) : ""}</td>
                <td className="px-1 py-0.5 text-right text-slate-500">{l.tps > 0 ? l.tps.toFixed(2) : ""}</td>
                <td className="px-1 py-0.5 text-right text-slate-500">{l.tvq > 0 ? l.tvq.toFixed(2) : ""}</td>
                <td className={`px-1 py-0.5 text-right font-semibold text-slate-700 ${isNonPayee ? "bg-red-50" : ""}`}>{l.total_avec_taxes > 0 ? l.total_avec_taxes.toFixed(2) : ""}</td>
                <td className="px-1 py-0.5">
                  <div className="flex items-center gap-0.5 justify-end">
                    {/* Non-payée toggle */}
                    {!isDelegated && !isFromDelegate && (
                      <button title={isNonPayee ? "Marquer comme payée" : "Marquer non payée"}
                        onClick={() => updateLigne(idx, "non_payee", !l.non_payee)}
                        className={`p-0.5 rounded transition-colors ${isNonPayee ? "text-orange-500 hover:text-orange-700" : "text-slate-300 hover:text-orange-400"}`}>
                        <AlertTriangle className="w-3 h-3" />
                      </button>
                    )}
                    {/* SC délégué reçu: choix Payé/Non payée (admin/proprio seulement) */}
                    {isFromDelegate && (l.service_call > 0 || l.km_montant > 0) && !isInstallateur && (
                      <div className="flex items-center gap-0.5">
                        {/* Payé cette semaine */}
                        <button
                          title={l.sc_paye_cette_semaine ? "Payé cette semaine (cliquer pour annuler)" : "Payer cette semaine"}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (l.sc_paye_cette_semaine) {
                              updateLigneMulti(idx, { sc_paye_cette_semaine: false, non_payee: false, sc_decision_prise: false });
                            } else {
                              updateLigneMulti(idx, { sc_paye_cette_semaine: true, non_payee: false, non_payee_resolue: false, sc_decision_prise: true });
                            }
                          }}
                          data-testid={`sc-paye-semaine-${idx}`}
                          className={`p-0.5 rounded transition-colors text-xs flex items-center gap-0.5 ${
                            l.sc_paye_cette_semaine
                              ? "text-emerald-600 hover:text-emerald-800 bg-emerald-50"
                              : "text-slate-400 hover:text-emerald-500"
                          }`}>
                          <DollarSign className="w-3 h-3" />
                        </button>
                        {/* Non payée */}
                        <button
                          title={l.non_payee ? "Marqué non payée" : "Mettre en non payée"}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (l.non_payee) {
                              updateLigneMulti(idx, { non_payee: false, sc_decision_prise: false });
                            } else {
                              updateLigneMulti(idx, { non_payee: true, sc_paye_cette_semaine: false, sc_decision_prise: true });
                            }
                          }}
                          data-testid={`sc-non-payee-${idx}`}
                          className={`p-0.5 rounded transition-colors ${
                            l.non_payee
                              ? "text-orange-500 hover:text-orange-700 bg-orange-50"
                              : "text-slate-300 hover:text-orange-400"
                          }`}>
                          <AlertTriangle className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {/* Indicateur visuel pour sous-traitants (spectateur) */}
                    {isFromDelegate && (l.service_call > 0 || l.km_montant > 0) && isInstallateur && (
                      <span className={`text-xs px-1 rounded ${
                        l.sc_paye_cette_semaine ? "text-emerald-600 bg-emerald-50" :
                        l.non_payee ? "text-orange-600 bg-orange-50" :
                        "text-slate-400 bg-slate-50"
                      }`}>
                        {l.sc_paye_cette_semaine ? "$" : l.non_payee ? "!" : "?"}
                      </span>
                    )}
                    {/* Delegate button */}
                    {!isFromDelegate && !isInstallateur && (
                      <div className="relative">
                        <button title={isDelegated ? `Délégué à ${l.delegue_a_nom}` : "Déléguer à un autre installateur"}
                          onClick={() => setDelegateRowId(delegateRowId === l.id ? null : l.id)}
                          className={`p-0.5 rounded transition-colors ${isDelegated ? "text-slate-500 hover:text-slate-700" : "text-slate-300 hover:text-blue-500"}`}>
                          <UserX className="w-3 h-3" />
                        </button>
                        {delegateRowId === l.id && (
                          <div className="absolute right-0 top-5 z-20 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[160px] py-1">
                            <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b border-slate-100">Déléguer à :</div>
                            <button onClick={() => { updateLigneMulti(idx, { delegue_a_id: "", delegue_a_nom: "" }); setDelegateRowId(null); }}
                              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${!l.delegue_a_id ? "font-semibold text-[#1a237e]" : ""}`}>
                              Aucun (annuler)
                            </button>
                            {allInstallers.filter(i => i.id !== currentInstallerId).length === 0 ? (
                              <p className="px-3 py-2 text-xs text-slate-400 italic">Aucun autre installateur disponible. Ajoutez-en dans la gestion des sous-traitants.</p>
                            ) : allInstallers.filter(i => i.id !== currentInstallerId).map(inst => (
                              <button key={inst.id}
                                onClick={() => { updateLigneMulti(idx, { delegue_a_id: inst.id, delegue_a_nom: `${inst.prenom} ${inst.nom}` }); setDelegateRowId(null); }}
                                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 ${l.delegue_a_id===inst.id ? "font-semibold text-[#1a237e]" : ""}`}>
                                {inst.prenom} {inst.nom}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Move up/down */}
                    {!isFromDelegate && !isInstallateur && (
                      <div className="flex flex-col -my-0.5">
                        <button onClick={() => onMoveRow(idx, -1)} disabled={idx === 0}
                          className={`text-slate-300 hover:text-blue-500 transition-colors p-0 leading-none ${idx === 0 ? 'opacity-20 cursor-default' : ''}`}
                          title="Monter">
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button onClick={() => onMoveRow(idx, 1)} disabled={idx === lignes.length - 1}
                          className={`text-slate-300 hover:text-blue-500 transition-colors p-0 leading-none ${idx === lignes.length - 1 ? 'opacity-20 cursor-default' : ''}`}
                          title="Descendre">
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {/* Delete */}
                    {!isFromDelegate && !isInstallateur && (
                      <button onClick={() => onDeleteRow(idx)} className="text-slate-300 hover:text-red-400 transition-colors p-0.5">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        {/* Totals row */}
        <tfoot>
          <tr className="bg-slate-800 text-white text-xs font-semibold border-t-2 border-slate-700">
            <td colSpan={3} className="px-2 py-2">TOTAUX</td>
            <td className="px-2 py-2 text-right bg-red-900">{totaux.total_service_calls > 0 ? totaux.total_service_calls.toFixed(2) : "—"}</td>
            <td className="px-2 py-2 text-right">{totaux.total_km > 0 ? totaux.total_km.toFixed(2) : "—"}</td>
            <td className="px-2 py-2 text-right text-red-300 pointer-events-auto" title="Cliquez pour le détail">
              <button type="button" className="underline decoration-dotted hover:text-white" data-testid="deductions-total-clic" onClick={onDetailDeductions} title="Cliquez pour le détail">
                {dedTotalHT > 0 ? `${dedTotalHT.toFixed(2)} HT` : "—"}
                <span className="block text-[10px] font-normal" data-testid="deductions-compteur">Déductions{nbDeductions > 0 ? ` (${nbDeductions})` : ''}</span>
              </button>
            </td>
            <td></td>
            <td className="px-2 py-2 text-right">{totaux.total_contrats > 0 ? totaux.total_contrats.toFixed(2) : "—"}</td>
            <td className="px-2 py-2 text-right bg-red-900">{totaux.total_extra_installateur > 0 ? totaux.total_extra_installateur.toFixed(2) : "—"}</td>
            <td className="px-2 py-2 text-right bg-red-900">{totaux.total_installateur_brut > 0 ? totaux.total_installateur_brut.toFixed(2) : "—"}</td>
            <td className="px-2 py-2 text-right">{totaux.tps_total > 0 ? totaux.tps_total.toFixed(2) : "—"}</td>
            <td className="px-2 py-2 text-right">{totaux.tvq_total > 0 ? totaux.tvq_total.toFixed(2) : "—"}</td>
            <td className="px-2 py-2 text-right bg-red-900">{totaux.total_avec_taxes > 0 ? totaux.total_avec_taxes.toFixed(2) : "—"}</td>
            <td></td>
          </tr>
          {/* NET row */}
          <tr className="bg-[#1a237e] text-white text-xs font-bold">
            <td colSpan={6} className="px-2 py-2">
              NET A PAYER (apres deductions)
              {dedTotalHT > 0 && (
                <span className="text-blue-300 font-normal ml-2">
                  Ded. HT: {dedTotalHT.toFixed(2)}$ → TTC: {(dedTotalHT * (1 + TPS + TVQ)).toFixed(2)}$
                  {totaux.total_deductions_auto > 0 && <span className="ml-1">(dont auto {totaux.total_deductions_auto.toFixed(2)}$ HT)</span>}
                </span>
              )}
            </td>
            <td></td>
            <td colSpan={2} className="px-2 py-2 text-right">
              <span className="text-xs text-blue-200">TPS {totaux.tps_total.toFixed(2)} + TVQ {totaux.tvq_total.toFixed(2)}</span>
            </td>
            <td colSpan={3} className="px-2 py-2 text-right text-base">{totaux.total_installateur_net.toFixed(2)}</td>
            <td className="px-2 py-2 text-right font-bold">{totaux.total_avec_taxes.toFixed(2)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div className="p-3 border-t border-slate-200 bg-slate-50">
        {!isInstallateur && (
          <button onClick={onAddRow} className="flex items-center gap-1.5 text-xs text-[#1a237e] font-medium hover:text-[#283593] transition-colors">
            <Plus className="w-3.5 h-3.5" />
            Ajouter une ligne
          </button>
        )}
      </div>
    </div>
  );
}

// ========== PRICING ITEM ROW (complex pricing logic) ==========
function PricingItemRow({ item, onAdd }) {
  // 🆕 [Phase 65 — 2026-06-18] Quantité non forcée — champ vide par défaut
  // pour éviter "1 → effacer → taper la vraie qté" (frustrant).
  // L'utilisateur tape direct la qté désirée. Bouton "Ajouter" désactivé si vide/0.
  // Après ajout : reset à "" (pas à "1") pour rester cohérent.
  const [qty, setQty] = useState("");
  const [sizeIdx, setSizeIdx] = useState(0);
  const [heightSurcharge, setHeightSurcharge] = useState(false);
  const [sqft, setSqft] = useState("");
  const [kmVariantIdx, setKmVariantIdx] = useState(0);
  const [customPrice, setCustomPrice] = useState(0);

  const variants = item.prix_variants || [];
  const selectedVariant = variants[sizeIdx] || variants[0] || null;
  const basePrice = selectedVariant ? selectedVariant.prix : item.prix;
  const isCustomKm = selectedVariant?.is_custom;

  // Parsing tolérant : vide / NaN → 0 (l'ajout est bloqué par canAdd ci-dessous)
  const qtyParsed = parseInt(qty, 10);
  const qtyNum = Number.isFinite(qtyParsed) && qtyParsed > 0 ? qtyParsed : 0;
  const sqftNum = parseFloat(sqft) || 0;

  let total = 0;
  if (item.type === "fixed") total = item.prix;
  else if (item.type === "quantity") total = item.prix * qtyNum;
  else if (item.type === "size_variant") total = basePrice * qtyNum + (heightSurcharge ? (item.height_surcharge_prix || 0) : 0);
  else if (item.type === "per_sqft") total = item.prix * sqftNum;
  else if (item.type === "km_range") total = isCustomKm ? customPrice : (basePrice || 0);

  // 🆕 [Phase 65] Désactiver l'ajout tant que la qté est vide/0 (ou sqft si per_sqft).
  // Pour fixed/km_range : pas de qté requise → toujours actif.
  const needsQty = item.type === "quantity" || item.type === "size_variant";
  const canAdd = (
    (item.type === "per_sqft" && sqftNum > 0) ||
    (needsQty && qtyNum > 0) ||
    (item.type === "fixed") ||
    (item.type === "km_range" && (!isCustomKm || customPrice > 0))
  );

  const handleAdd = () => {
    if (!canAdd) {
      if (needsQty && qtyNum <= 0) {
        toast.error("Entrez une quantité supérieure à 0");
      } else if (item.type === "per_sqft" && sqftNum <= 0) {
        toast.error(`Entrez une valeur ${item.unite || "qté"} supérieure à 0`);
      }
      return;
    }
    let label = item.service;
    if (item.type === "quantity" && qtyNum > 1) label += ` × ${qtyNum}`;
    if (item.type === "size_variant" && selectedVariant) label += ` (${selectedVariant.label})${qtyNum > 1 ? ` × ${qtyNum}` : ""}`;
    if (item.type === "per_sqft") label += ` (${sqftNum} ${item.unite || "unités"})`;
    if (item.type === "km_range" && selectedVariant) label += ` — ${selectedVariant.label}`;
    if (heightSurcharge) label += ` + surcharge hauteur`;
    // 🆕 [Phase 87 — 2026-07-07] Fix bug prix Marché du Store :
    //   - Envoyer `basePrice` (prix de la gamme sélectionnée) au lieu de `item.prix`
    //     (qui est toujours le prix de base = 1re gamme). Sans ce fix, choisir la
    //     gamme "101" et plus" à 150.50 $ retombait à 63 $ (bug #09).
    //   - `total` reflète déjà correctement la sélection + surcharge hauteur.
    onAdd({ id: item.id, service: label, service_name: label, prix: basePrice, total, quantite: qtyNum > 0 ? qtyNum : 1 });
    // 🆕 [Phase 65] Reset à VIDE (pas à "1") pour rester cohérent
    setQty("");
    setSqft("");
    setHeightSurcharge(false);
  };

  return (
    <div className="px-4 py-3 hover:bg-slate-50 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium text-sm text-slate-900">{item.service}</p>
          {item.categorie && <p className="text-xs text-slate-400 mb-2">{item.categorie}</p>}
          {/* Controls */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Size variants */}
            {(item.type === "size_variant" || item.type === "km_range") && variants.length > 0 && (
              <select value={sizeIdx} onChange={e => { setSizeIdx(parseInt(e.target.value)); setKmVariantIdx(parseInt(e.target.value)); }}
                className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1a237e]/30 bg-white">
                {variants.map((v, i) => <option key={`variant-${i}`} value={i}>{v.label} — {v.is_custom ? "Sur demande" : `${fmt(v.prix)}`}</option>)}
              </select>
            )}
            {/* Quantity */}
            {item.has_quantity && item.type !== "per_sqft" && item.type !== "km_range" && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500">Qté :</span>
                <input type="number" min="1" step="1" value={qty}
                  onChange={e => setQty(e.target.value)}
                  placeholder="Qté"
                  data-testid={`lmds-extra-qty-${item.id}`}
                  className="w-12 text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none text-center" />
              </div>
            )}
            {/* Per sq ft — 🆕 2026-05-12 saisie décimale libre + champ vide possible */}
            {item.type === "per_sqft" && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500">{item.unite || "qté"} :</span>
                <input type="number" min="0" step="0.01" value={sqft}
                  onChange={e => setSqft(e.target.value)}
                  placeholder="0"
                  data-testid={`lmds-extra-sqft-${item.id}`}
                  className="w-20 text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none text-center" />
              </div>
            )}
            {/* Custom km price */}
            {item.type === "km_range" && isCustomKm && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500">Montant :</span>
                <input type="number" min="0" step="0.01" value={customPrice} onChange={e => setCustomPrice(parseFloat(e.target.value)||0)}
                  className="w-20 text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none text-center" />
              </div>
            )}
            {/* Height surcharge */}
            {item.has_height_surcharge && (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={heightSurcharge} onChange={e => setHeightSurcharge(e.target.checked)} className="rounded" />
                <span className="text-xs text-slate-600">{item.height_surcharge_label || `+ ${fmt(item.height_surcharge_prix || 0)}`}</span>
              </label>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 min-w-[80px]">
          <span className="font-bold text-[#1a237e] text-sm">{isCustomKm && !customPrice ? "Sur demande" : fmt(total)}</span>
          <button onClick={handleAdd}
            disabled={!canAdd}
            data-testid={`lmds-extra-add-${item.id}`}
            className="text-xs bg-[#1a237e] text-white px-3 py-1 rounded-lg hover:bg-[#283593] transition-colors font-medium whitespace-nowrap disabled:bg-slate-300 disabled:cursor-not-allowed">
            + Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ========== MAIN PAGE ==========
export function LMDSInstallateur() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isInstallateur, isProprietaire } = useAuth();
  const { canDo } = usePermissions();
  
  // 🆕 Permissions LMDS
  // ✅ Le propriétaire bypasse la restriction "voir son propre profil seulement"
  const voirPropreProfil = !isProprietaire && canDo('lmds_voir_propre_profil_seulement');
  const peutSauvegarderRapport = canDo('lmds_rapports_sauvegarder');
  const peutEnvoyerRapport = canDo('lmds_rapports_envoyer');
  const peutModifierTableau = canDo('lmds_rapports_modifier_tableau');
  const peutAjouterDocuments = canDo('lmds_rapports_ajouter_documents');
  const peutSupprimerDocuments = isProprietaire || canDo('lmds_rapports_supprimer_documents');
  const peutAjouterBonus = canDo('lmds_bonus_ajouter');
  
  const curYear = new Date().getFullYear();

  const [installateur, setInstallateur] = useState(null);
  const [tab, setTab] = useState("rapports");
  const [searchParams, setSearchParams] = useSearchParams();  // 🧾 Phase 5 : ?annee=&semaine=&ligne= (lien d'un résultat de recherche)
  const urlAnnee = parseInt(searchParams.get("annee")) || null;
  const urlSemaine = parseInt(searchParams.get("semaine")) || null;
  const [selectedYear, setSelectedYear] = useState(urlAnnee || curYear);
  const [selectedWeek, setSelectedWeek] = useState(urlSemaine || getCurrentWeek(urlAnnee || curYear));
  const [ligneSurlignee, setLigneSurlignee] = useState(searchParams.get("ligne") || null);
  useEffect(() => {
    const a = parseInt(searchParams.get("annee")), w = parseInt(searchParams.get("semaine"));
    if (a && w) { setSelectedYear(a); setSelectedWeek(w); setTab("rapports"); }
    setLigneSurlignee(searchParams.get("ligne") || null);
  }, [searchParams]);
  const ouvrirResultat = (lien) => {
    const q = `?annee=${lien.annee}&semaine=${lien.semaine}&ligne=${lien.ligne_id}`;
    if (lien.installateur_id === id) setSearchParams({ annee: String(lien.annee), semaine: String(lien.semaine), ligne: lien.ligne_id });
    else navigate(`/sous-traitants/lmds/${lien.installateur_id}${q}`);
  };
  const [semaineMeta, setSemaineMeta] = useState([]);
  const [lignes, setLignes] = useState(() => Array.from({length:15}, newLigne));
  useEffect(() => {
    if (!ligneSurlignee) return;
    const el = document.getElementById(`ligne-${ligneSurlignee}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Le surlignage reste jusqu'à la PROCHAINE ACTION de l'utilisatrice (clic, défilement manuel, touche, changement de semaine) — pas de minuteur.
    const fin = () => { setLigneSurlignee(null); setSearchParams({}, { replace: true }); };
    const arme = setTimeout(() => {
      window.addEventListener("pointerdown", fin, { once: true });
      window.addEventListener("keydown", fin, { once: true });
      window.addEventListener("wheel", fin, { once: true, passive: true });
      window.addEventListener("touchmove", fin, { once: true, passive: true });
    }, 1200);  // laisse finir le défilement automatique avant d'armer les écouteurs
    return () => { clearTimeout(arme); ["pointerdown", "keydown", "wheel", "touchmove"].forEach((e) => window.removeEventListener(e, fin)); };
  }, [ligneSurlignee, lignes.length, setSearchParams]);
  const [rapportData, setRapportData] = useState({});
  const [rapportAttachments, setRapportAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmRapportTermine, setConfirmRapportTermine] = useState(false);
  const [detailDeductionsOuvert, setDetailDeductionsOuvert] = useState(false);  // 🧾 Phase 2
  const [nbDeductions, setNbDeductions] = useState(0);  // 🧾 compteur « Déductions (N) » = nb_elements (manuel + toutes occurrences de la semaine)
  useEffect(() => {
    if (!id || !selectedYear || !selectedWeek) return;
    let actif = true;
    axios.get(`${API}/api/lmds/rapports/${id}/${selectedYear}/${selectedWeek}/deductions-detail`)
      .then((r) => { if (actif) setNbDeductions(r.data?.nb_elements || 0); }).catch(() => { if (actif) setNbDeductions(0); });
    return () => { actif = false; };
  }, [id, selectedYear, selectedWeek, rapportData.total_deductions_auto, rapportData.total_deductions, detailDeductionsOuvert]);
  const [sendingRapportTermine, setSendingRapportTermine] = useState(false);

  // EM-050: Auto-save every 60 seconds (NOT for installateurs — they are spectators)
  useEffect(() => {
    if (!id || !selectedYear || !selectedWeek || isInstallateur) return;
    const timer = setInterval(() => {
      if (!saving && lignes.some(l => l.nom_client || l.total)) {
        saveRapport();
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [id, selectedYear, selectedWeek, lignes, saving, isInstallateur]);

  // Resume tab
  const [resume, setResume] = useState(null);

  // Facturation tab
  const [factureMois, setFactureMois] = useState(new Date().getMonth() + 1);
  const [facture, setFacture] = useState(null);
  const [sendFactureEmail, setSendFactureEmail] = useState("");
  const [sendingFacture, setSendingFacture] = useState(false);

  // Soumettre SC tab
  const [pricingChart, setPricingChart] = useState([]);
  const [pendingSCs, setPendingSCs] = useState([]);
  const [scForm, setScForm] = useState({ nom_client:"", date_rdv:"", si:"", km:0, notes:"", services:[] });
  const [submittingSC, setSubmittingSC] = useState(false);
  const [showPricingSelector, setShowPricingSelector] = useState(false);

  // Bonus tab
  const [bonusData, setBonusData] = useState(null);
  const [loadingBonus, setLoadingBonus] = useState(false);
  const [showAddRef, setShowAddRef] = useState(false);
  const [refForm, setRefForm] = useState({ client_nom: "", date: "", notes: "" });

  // Delegation + non-payée
  const [allInstallers, setAllInstallers] = useState([]);
  const [nonPayees, setNonPayees] = useState([]);
  // Row selection (surlignage type Excel)
  const [selectedRowId, setSelectedRowId] = useState(null);

  const taux = installateur?.taux || 0.65;

  // Load installer
  useEffect(() => {
    axios.get(`${API}/api/lmds/installers/${id}`)
      .then(r => r.status === 200 ? r.data : Promise.reject())
      .then(data => {
        // 🆕 Si l'utilisateur ne peut voir que son propre profil, vérifier l'ID
        if (voirPropreProfil && user?.employee_id && data.id !== user.employee_id) {
          toast.error("Vous ne pouvez voir que votre propre profil");
          navigate(`/sous-traitants/lmds/${user.employee_id}`);
          return;
        }
        setInstallateur(data);
      })
      .catch(() => { toast.error("Installateur introuvable"); navigate("/sous-traitants/lmds"); });
  }, [id, voirPropreProfil, user, navigate]);

  // Load semaine meta
  useEffect(() => {
    if (!installateur) return;
    axios.get(`${API}/api/lmds/rapports-meta/${id}/${selectedYear}`)
      .then(r => r.data).then(setSemaineMeta).catch(() => {});
  }, [id, selectedYear, installateur]);

  // Load selected week rapport
  useEffect(() => {
    if (!installateur) return;
    setLoading(true);
    axios.get(`${API}/api/lmds/rapports/${id}/${selectedYear}/${selectedWeek}`)
      .then(r => r.data)
      .then(data => {
        setRapportData(data);
        setRapportAttachments(data.attachments || []);
        if (data.lignes && data.lignes.length > 0) {
          const withCalc = data.lignes.map(l => calcLigne(l, taux));
          const padded = [...withCalc];
          while (padded.length < 15) padded.push(newLigne());
          setLignes(padded);
        } else {
          setLignes(Array.from({length:15}, newLigne));
        }
      })
      .catch(() => setLignes(Array.from({length:15}, newLigne)))
      .finally(() => setLoading(false));
  }, [id, selectedYear, selectedWeek, installateur]);

  // Load facture
  useEffect(() => {
    if (tab !== "facturation" || !installateur) return;
    axios.get(`${API}/api/lmds/facture/${id}/${selectedYear}/${factureMois}`)
      .then(r => r.data).then(setFacture).catch(() => {});
  }, [tab, id, selectedYear, factureMois, installateur]);

  // Access control: installer can only see their own page
  useEffect(() => {
    if (isInstallateur && user?.employee_id && user.employee_id !== id) {
      navigate(`/sous-traitants/lmds/${user.employee_id}`, { replace: true });
    }
  }, [isInstallateur, user, id, navigate]);

  // Load all installers (for delegation feature)
  useEffect(() => {
    axios.get(`${API}/api/lmds/installers`).then(r => {
      const data = r.data;
      if (Array.isArray(data)) setAllInstallers(data);
      else if (data?.installers) setAllInstallers(data.installers);
      else console.warn('Format inattendu pour allInstallers:', data);
    }).catch(err => console.error('Erreur chargement installateurs LMDS:', err));
  }, []);

  // Load non-payées
  useEffect(() => {
    if (!installateur) return;
    axios.get(`${API}/api/lmds/non-payees/${id}`).then(r => r.data).then(setNonPayees).catch(() => {});
  }, [installateur, id]);
  useEffect(() => {
    if (tab !== "soumettre" || !installateur) return;
    axios.get(`${API}/api/lmds/pricing-chart`).then(r => r.data).then(setPricingChart).catch(() => {});
    axios.get(`${API}/api/lmds/pending-sc/${id}`).then(r => r.data).then(setPendingSCs).catch(() => {});
  }, [tab, id, installateur]);

  // Load resume
  useEffect(() => {
    if (tab !== "resume" || !installateur) return;
    setResume(null);
    axios.get(`${API}/api/lmds/resume/${id}/${selectedYear}`)
      .then(r => r.data).then(setResume).catch(() => {});
  }, [tab, id, selectedYear, installateur]);

  // Load bonus
  useEffect(() => {
    if (tab !== "bonus" || !installateur) return;
    setLoadingBonus(true);
    setBonusData(null);
    axios.get(`${API}/api/lmds/bonus/${id}/${selectedYear}`)
      .then(r => r.data).then(setBonusData).catch(() => {})
      .finally(() => setLoadingBonus(false));
  }, [tab, id, selectedYear, installateur]);

  const { start: wStart, end: wEnd } = getWeekDates(selectedYear, selectedWeek);
  const weekLabel = `${formatFull(wStart)} au ${formatFull(wEnd)}`;

  async function saveRapport() {
    setSaving(true);
    try {
      const nonEmpty = lignes.filter(l => l.nom_client || l.total || l.service_call || l.deductions);
      const payload = {
        ...rapportData,
        lignes: nonEmpty,
        date_debut: formatFull(wStart),
        date_fin: formatFull(wEnd),
        rapport_envoye: rapportData.rapport_envoye || false,
      };
      const res = await axios.put(`${API}/api/lmds/rapports/${id}/${selectedYear}/${selectedWeek}`, payload);
      const saved = res.data;
      setRapportData(saved);
      // Refresh meta
      const metaRes = await axios.get(`${API}/api/lmds/rapports-meta/${id}/${selectedYear}`);
      setSemaineMeta(metaRes.data);
      toast.success("Rapport sauvegardé ✓");
    } catch { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  }

  async function handleRapportTermine() {
    setSendingRapportTermine(true);
    try {
      // 🐛 [Bug prod 2026-07-16] auth_token (pas token) — cohérent AuthContext
      const authTok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const res = await axios.post(`${API}/api/lmds/rapports/${id}/${selectedYear}/${selectedWeek}/terminer`, null, {
        headers: { Authorization: `Bearer ${authTok}` },
      });
      const data = res.data;
      if (data.success) {
        toast.success("SMS envoyé à l'installateur ✓");
        setRapportData(p => ({ ...p, rapport_envoye: true }));
        const metaRes = await axios.get(`${API}/api/lmds/rapports-meta/${id}/${selectedYear}`);
        setSemaineMeta(metaRes.data);
      } else {
        toast.error(data.error || "Erreur envoi SMS");
      }
    } catch { toast.error("Erreur envoi SMS"); }
    finally { setSendingRapportTermine(false); setConfirmRapportTermine(false); }
  }

  async function sendReport() {
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/lmds/rapports/${id}/${selectedYear}/${selectedWeek}/envoyer`);
      const data = res.data;
      if (data.success) {
        toast.success(`Rapport envoyé à ${installateur.email} ✓`);
        setRapportData(p => ({ ...p, rapport_envoye: true }));
      } else {
        toast.error(data.error || "Erreur d'envoi");
      }
    } catch { toast.error("Erreur d'envoi"); }
    finally { setSending(false); }
  }

  async function sendFacture() {
    setSendingFacture(true);
    try {
      const res = await axios.post(`${API}/api/lmds/facture/${id}/${selectedYear}/${factureMois}/envoyer`, {
        to_email: sendFactureEmail || undefined
      });
      const data = res.data;
      if (data.success) toast.success("Facture envoyée ✓");
      else toast.error(data.error || "Erreur d'envoi");
    } catch { toast.error("Erreur d'envoi"); }
    finally { setSendingFacture(false); }
  }

  async function submitSC() {
    if (!scForm.nom_client || !scForm.date_rdv || scForm.services.length === 0) {
      toast.error("Remplissez le client, la date et au moins un service");
      return;
    }
    setSubmittingSC(true);
    try {
      const total = scForm.services.reduce((s, sv) => s + sv.total, 0) + (parseFloat(scForm.km)||0);
      await axios.post(`${API}/api/lmds/pending-sc`, {
        ...scForm, installateur_id: id, total
      });
      toast.success("SC soumis ✓ — L'adjoint sera notifié");
      setScForm({ nom_client:"", date_rdv:"", si:"", km:0, notes:"", services:[] });
      const r = await axios.get(`${API}/api/lmds/pending-sc/${id}`);
      setPendingSCs(r.data);
    } catch { toast.error("Erreur de soumission"); }
    finally { setSubmittingSC(false); }
  }

  function addService(item) {
    // 🆕 [Phase 87 — 2026-07-07] Fix bug prix Marché du Store :
    //   - Respecter `item.quantite` et `item.total` reçus (au lieu de les écraser à 1 et item.prix).
    //   - `item.prix` provient de `handleAdd` (Phase 87) et vaut désormais `basePrice`
    //     (prix de la gamme sélectionnée, plus le prix de base de la 1re gamme).
    //   - Utiliser `item.service_name` (label enrichi avec gamme + qté) plutôt que `item.service` (nom générique).
    //   - Branche "existing" : additionner la nouvelle quantité et adopter le nouveau prix
    //     (si l'utilisatrice a changé de gamme entre 2 ajouts, le dernier prix prime).
    const qteAjoutee = item.quantite || 1;
    const prixItem = item.prix;
    const totalRecu = (typeof item.total === 'number') ? item.total : r2(prixItem * qteAjoutee);
    setScForm(p => {
      const existing = p.services.find(s => s.service_id === item.id);
      if (existing) {
        const nouvelleQte = existing.quantite + qteAjoutee;
        return { ...p, services: p.services.map(s => s.service_id === item.id ? {
          ...s,
          service_name: item.service_name || item.service || s.service_name,
          prix: prixItem,
          quantite: nouvelleQte,
          total: r2(nouvelleQte * prixItem),
        } : s) };
      }
      return { ...p, services: [...p.services, {
        service_id: item.id,
        service_name: item.service_name || item.service,
        categorie: item.categorie,
        prix: prixItem,
        quantite: qteAjoutee,
        total: totalRecu,
      }] };
    });
    setShowPricingSelector(false);
  }

  function removeService(svcId) {
    setScForm(p => ({ ...p, services: p.services.filter(s => s.service_id !== svcId) }));
  }

  async function deletePendingSC(scId) {
    await axios.delete(`${API}/api/lmds/pending-sc/${scId}`);
    const r = await axios.get(`${API}/api/lmds/pending-sc/${id}`);
    setPendingSCs(r.data);
    toast.success("Soumission supprimée");
  }

  // 🆕 [Phase 87 — 2026-07-07] Approuver un Extra (Owner/Admin only).
  // Backend: PATCH /api/lmds/pending-sc/:id/approuver
  // Retourne action ∈ {creation, fusion} + service_call_id + semaine/annee ciblés.
  async function approuverPendingSC(scId) {
    try {
      const resp = await axios.patch(`${API}/api/lmds/pending-sc/${scId}/approuver`);
      const { action, nom_client, date_rdv, semaine, annee } = resp.data;
      const label = action === "fusion"
        ? `✅ Extra ajouté au service call existant du ${date_rdv} pour ${nom_client}`
        : `✅ Nouveau service call créé pour ${nom_client} le ${date_rdv} (S${semaine}/${annee})`;
      toast.success(label);
      // Refresh liste
      const r = await axios.get(`${API}/api/lmds/pending-sc/${id}`);
      setPendingSCs(r.data);
      // Optionnel : reload rapport de la semaine s'il est actuellement affiché
      if (selectedYear === annee && selectedWeek === semaine) {
        const rr = await axios.get(`${API}/api/lmds/rapports/${id}/${annee}/${semaine}`);
        if (rr.data) setRapport(rr.data);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur lors de l'approbation";
      toast.error(msg);
    }
  }

  async function addBonusRef() {
    if (!refForm.client_nom) { toast.error("Nom du client requis"); return; }
    try {
      await axios.post(`${API}/api/lmds/bonus-refs`, {
        ...refForm, installateur_id: id, montant: 100
      });
      toast.success("Bonus référence ajouté ✓");
      setShowAddRef(false);
      setRefForm({ client_nom: "", date: "", notes: "" });
      const r = await axios.get(`${API}/api/lmds/bonus/${id}/${selectedYear}`);
      setBonusData(r.data);
    } catch { toast.error("Erreur"); }
  }

  async function deleteBonusRef(refId) {
    await axios.delete(`${API}/api/lmds/bonus-refs/${refId}`);
    toast.success("Bonus supprimé");
    const r = await axios.get(`${API}/api/lmds/bonus/${id}/${selectedYear}`);
    setBonusData(r.data);
  }

  const handleUploadAttachment = async (files) => {
    if (!files.length) return;
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/api/lmds/rapports/${id}/${selectedYear}/${selectedWeek}/attachments`, fd);
      const data = res.data;
      if (res.status === 200) {
        setRapportAttachments(prev => [...prev, ...data.attachments]);
        toast.success(`${data.attachments.length} fichier(s) ajouté(s)`);
      } else { toast.error(data.detail || 'Erreur upload'); }
    } catch (e) { 
      const data = e.response?.data;
      toast.error(data?.detail || 'Erreur réseau'); 
    }
    finally { setUploadingAttachment(false); }
  };

  const handleDeleteAttachment = async (attId) => {
    try {
      const res = await axios.delete(`${API}/api/lmds/rapports/${id}/${selectedYear}/${selectedWeek}/attachments/${attId}`);
      if (res.status === 200) {
        setRapportAttachments(prev => prev.filter(a => a.id !== attId));
        toast.success('Fichier supprimé');
      } else { toast.error('Erreur suppression'); }
    } catch { toast.error('Erreur réseau'); }
  };

  if (!installateur) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]" /></div>;
  }

  const totaux = calcTotaux(lignes, taux, rapportData.total_deductions_auto || 0);

  return (
    <div className="space-y-5 max-w-full overflow-x-auto">
      {/* Modal confirmation Rapport terminé */}
      {confirmRapportTermine && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Envoyer le rapport</h3>
            <p className="text-gray-600 mb-3">
              Cela va <strong>terminer le rapport</strong>, l'envoyer par courriel et envoyer un SMS à l'installateur.
            </p>
            <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-4 py-3 mb-6">
              Un SMS + courriel seront envoyés à <strong>{installateur?.prenom} {installateur?.nom}</strong> pour l'informer que son rapport de la semaine est prêt.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmRapportTermine(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm">
                Annuler
              </button>
              <button onClick={async () => { await handleRapportTermine(); if (installateur.email) await sendReport(); }} disabled={sendingRapportTermine}
                data-testid="confirm-rapport-termine"
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-medium text-sm disabled:opacity-50">
                {sendingRapportTermine ? "Envoi en cours..." : "Confirmer et envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/sous-traitants/lmds")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />Sous-traitants LMDS
          </button>
          <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30">
            {[curYear-1, curYear, curYear+1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1a237e] flex items-center justify-center text-white font-semibold shrink-0">
            {installateur.prenom?.[0]}{installateur.nom?.[0]}
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">{installateur.prenom} {installateur.nom}</h1>
            <p className="text-slate-500 text-xs truncate">{Math.round(taux*100)}% des contrats · {installateur.email || "Pas de courriel"}</p>
          </div>
        </div>
        <div className="w-full md:w-96 md:ml-auto">
          <RechercheClientGlobale installateurId={isInstallateur ? id : null} onOuvrir={ouvrirResultat} />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-1 overflow-x-auto"  style={{WebkitOverflowScrolling: 'touch'}}>
          {[
            { key:"rapports", label:"Rapports" },
            { key:"resume", label:"Résumé" },
            { key:"soumettre", label:"Extras" },
            { key:"bonus", label:"Bonus" },
            { key:"performance", label:"Performance" },
            { key:"factures", label:"Mes factures" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab===t.key ? "border-[#1a237e] text-[#1a237e]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* TAB: RAPPORTS */}
      {tab === "rapports" && (
        <div className="space-y-4">
          {/* Week grid */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-slate-700">Sélectionner une semaine</p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200 inline-block"></span>Envoyé</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200 inline-block"></span>Sauvegardé</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-200 inline-block"></span>Vide</span>
              </div>
            </div>
            <WeekGrid year={selectedYear} selectedWeek={selectedWeek} semaineMeta={semaineMeta} onSelect={setSelectedWeek} />
          </div>

          {/* Week header */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedWeek(w => Math.max(1, w-1))} className="p-1.5 rounded hover:bg-slate-100 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div>
                  <p className="font-semibold text-slate-900">Semaine {selectedWeek}</p>
                  <p className="text-sm text-slate-500">{weekLabel}</p>
                </div>
                <button onClick={() => setSelectedWeek(w => Math.min(52, w+1))} className="p-1.5 rounded hover:bg-slate-100 transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {rapportData.rapport_envoye && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                    <CheckCircle2 className="w-3 h-3" />Rapport envoyé
                  </span>
                )}
                {peutSauvegarderRapport && (
                    <button onClick={saveRapport} disabled={saving} data-testid="save-rapport-btn"
                      className="flex items-center gap-2 bg-[#1a237e] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#283593] disabled:opacity-50 transition-colors">
                      <Save className="w-4 h-4" />
                      {saving ? "Sauvegarde..." : "Sauvegarder"}
                    </button>
                )}
                {peutEnvoyerRapport && (
                    <button onClick={() => setConfirmRapportTermine(true)} disabled={sendingRapportTermine || sending} data-testid="rapport-termine-btn"
                      title="Terminer le rapport, envoyer par courriel et SMS à l'installateur"
                      className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                      <Send className="w-4 h-4" />
                      {sendingRapportTermine || sending ? "Envoi..." : "Envoyer rapport"}
                    </button>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a237e]" /></div>
          ) : (
            <RapportTable
              lignes={lignes}
              taux={taux}
              onChange={peutModifierTableau ? setLignes : () => {}}
              onAddRow={peutModifierTableau ? () => setLignes(p => [...p, newLigne()]) : () => {}}
              onDeleteRow={peutModifierTableau ? idx => setLignes(p => p.filter((_,i) => i!==idx)) : () => {}}
              onMoveRow={peutModifierTableau ? (idx, dir) => setLignes(p => {
                const arr = [...p];
                const newIdx = idx + dir;
                if (newIdx < 0 || newIdx >= arr.length) return arr;
                [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
                return arr;
              }) : () => {}}
              allInstallers={allInstallers}
              currentInstallerId={id}
              selectedRowId={selectedRowId}
              setSelectedRowId={setSelectedRowId}
              isInstallateur={isInstallateur}
              deductionsAuto={rapportData.total_deductions_auto || 0}
              nbDeductions={nbDeductions}
              onDetailDeductions={() => setDetailDeductionsOuvert(true)}
              ligneSurlignee={ligneSurlignee}
            />
          )}
          <DetailDeductionsDialog ouvert={detailDeductionsOuvert} onFermer={() => setDetailDeductionsOuvert(false)}
            installateurId={id} annee={selectedYear} semaine={selectedWeek} />

          {/* Quick summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 min-w-0">
              <p className="text-xs text-slate-500">CA Installateur</p>
              <p className="text-base sm:text-lg font-bold text-[#1a237e] truncate">{fmt(totaux.total_installateur_brut)}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 min-w-0">
              <p className="text-xs text-slate-500">Deductions (HT)</p>
              <p className="text-base sm:text-lg font-bold text-red-600 truncate" data-testid="resume-deductions-ht">{fmt(totaux.total_deductions + totaux.total_deductions_auto)}</p>
              {(totaux.total_deductions + totaux.total_deductions_auto) > 0 && <p className="text-xs text-slate-400 truncate">TTC: {fmt((totaux.total_deductions + totaux.total_deductions_auto) * (1 + TPS + TVQ))}{totaux.total_deductions_auto > 0 ? ` · auto ${fmt(totaux.total_deductions_auto)} HT` : ''}</p>}
            </div>
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 shadow-sm p-3 min-w-0">
              <p className="text-xs text-emerald-700">Net à recevoir</p>
              <p className="text-base sm:text-lg font-bold text-emerald-700 truncate">{fmt(totaux.total_installateur_net)}</p>
              <p className="text-xs text-slate-400">avant taxes</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 min-w-0">
              <p className="text-xs text-slate-500">TPS (5%)</p>
              <p className="text-base sm:text-lg font-bold text-slate-700 truncate">{fmt(totaux.tps_total)}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 min-w-0">
              <p className="text-xs text-slate-500">TVQ (9.975%)</p>
              <p className="text-base sm:text-lg font-bold text-slate-700 truncate">{fmt(totaux.tvq_total)}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 shadow-sm p-3 min-w-0">
              <p className="text-xs text-emerald-700">Total avec taxes</p>
              <p className="text-base sm:text-lg font-bold text-emerald-700 truncate">{fmt(totaux.total_avec_taxes)}</p>
            </div>
          </div>

          {/* Attachments / Documents */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="section-attachments-lmds">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-slate-500" />
                <h4 className="text-sm font-semibold text-slate-700">Documents / Photos joints ({rapportAttachments.length})</h4>
              </div>
              {peutAjouterDocuments && (
              <label className="cursor-pointer">
                <input type="file" multiple className="hidden"
                  onChange={e => handleUploadAttachment(Array.from(e.target.files))}
                  disabled={uploadingAttachment}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" />
                <span className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                  data-testid="btn-upload-attachment-lmds">
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingAttachment ? 'Envoi...' : 'Ajouter'}
                </span>
              </label>
              )}
            </div>
            {rapportAttachments.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-4">Aucun document joint à ce rapport</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {rapportAttachments.map(att => {
                  const isImage = att.content_type?.startsWith('image/');
                  return (
                    <div key={att.id} className="flex items-center gap-2 border rounded-lg p-2 bg-slate-50 group">
                      {isImage ? (
                        <a href={urlFichier(att.url)} target="_blank" rel="noreferrer" className="shrink-0">
                          <img src={urlFichier(att.url)} alt={att.filename} className="w-12 h-12 object-cover rounded" />
                        </a>
                      ) : (
                        <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5 text-slate-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <a href={urlFichier(att.url)} target="_blank" rel="noreferrer" className="text-sm font-medium text-slate-700 hover:text-blue-600 truncate block">
                          {att.filename}
                        </a>
                        <p className="text-xs text-slate-400">{new Date(att.uploaded_at).toLocaleDateString('fr-CA')}</p>
                      </div>
                      <button onClick={() => handleDeleteAttachment(att.id)}
                        className={`text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 ${!peutSupprimerDocuments ? 'hidden' : ''}`}
                        data-testid={`btn-delete-att-${att.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Non-payées warning */}
          {nonPayees.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <h4 className="font-semibold text-orange-800 text-sm">Installations non payées ({nonPayees.length})</h4>
                <span className="text-xs text-orange-600 ml-auto">Auto-résolues lorsque le client + SI réapparaît sur un rapport</span>
              </div>
              <div className="space-y-1.5">
                {nonPayees.map(np => (
                  <div key={`${np.annee}-${np.semaine}-${np.id}`} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-orange-100">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-orange-700">Sem. {np.semaine} — {np.annee}</span>
                      <span className="text-sm font-medium text-slate-900">{np.nom_client}</span>
                      {np.si && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 rounded">SI: {np.si}</span>}
                      {np.date_rdv && <span className="text-xs text-slate-500">{np.date_rdv}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-700 text-sm">{fmt(np.total)}</span>
                      {!isInstallateur && (
                      <button
                        data-testid={`rappel-sms-btn-${np.ligne_id || np.id}`}
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/api/sms/rappel-non-payee/${id}/${np.ligne_id || np.id}`);
                            const d = r.data;
                            if (r.status === 200) toast.success("Rappel SMS envoyé");
                            else toast.error(d.detail || "Erreur envoi SMS");
                          } catch (e) { 
                            const d = e.response?.data;
                            toast.error(d?.detail || "Erreur envoi SMS"); 
                          }
                        }}
                        title="Envoyer rappel SMS"
                        className="flex items-center gap-1 text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-2 py-1 rounded transition-colors">
                        <MessageSquare className="w-3 h-3" />
                        SMS
                      </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {tab === "soumettre" && (
        <div className="space-y-4">
          {/* Pending SC list */}
          {pendingSCs.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">SC soumis en attente</h3>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{pendingSCs.filter(s=>s.statut==="en_attente").length} en attente</span>
              </div>
              <div className="divide-y divide-slate-100">
                {pendingSCs.map(sc => (
                  <div key={sc.id} className="px-4 py-3 flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900 text-sm">{sc.nom_client}</span>
                        <span className="text-xs text-slate-500">{sc.date_rdv}</span>
                        {sc.si && <span className="text-xs bg-slate-100 px-1.5 rounded">SI: {sc.si}</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.statut==="en_attente" ? "bg-amber-100 text-amber-700" : sc.statut==="approuve" ? "bg-emerald-100 text-emerald-700" : sc.statut==="valide" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`pending-sc-statut-${sc.id}`}>
                          {sc.statut === "en_attente" ? "En attente" : sc.statut === "approuve" ? "Approuvé" : sc.statut === "valide" ? "Validé" : "Refusé"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 space-y-0.5">
                        {sc.services?.map((s,i) => <span key={s.service_name || `service-${i}`} className="block">• {s.service_name} × {s.quantite} = {fmt(s.total)}</span>)}
                        {sc.km > 0 && <span className="block">• Kilométrage : {fmt(sc.km)}</span>}
                        {sc.notes && <span className="block italic text-slate-400">{sc.notes}</span>}
                        {sc.statut === "approuve" && sc.action_approbation && (
                          <span className="block text-emerald-700 font-medium mt-1" data-testid={`pending-sc-approbation-info-${sc.id}`}>
                            ✅ {sc.action_approbation === "fusion" ? "Fusionné dans service call existant" : "Nouveau service call créé"} — S{sc.semaine_rapport}/{sc.annee_rapport}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-[#1a237e]">{fmt(sc.total)}</span>
                      {/* 🆕 [Phase 87 — 2026-07-07] Bouton "Approuver" (Owner/Admin only, seulement si en_attente) */}
                      {isProprietaire && sc.statut === "en_attente" && (
                        <button
                          onClick={() => approuverPendingSC(sc.id)}
                          className="px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
                          data-testid={`pending-sc-approuver-${sc.id}`}
                          aria-label={`Approuver l'extra pour ${sc.nom_client}`}
                          title="Approuver et intégrer au rapport hebdomadaire"
                        >
                          ✅ Approuver
                        </button>
                      )}
                      <button onClick={() => deletePendingSC(sc.id)} className="text-slate-300 hover:text-red-400 transition-colors" data-testid={`pending-sc-delete-${sc.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New SC form */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Soumettre un extra</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Nom Client *</label>
                <input value={scForm.nom_client} onChange={e => setScForm(p=>({...p,nom_client:e.target.value}))}
                  placeholder="Nom du client..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Date RDV *</label>
                <input type="date" value={scForm.date_rdv} onChange={e => setScForm(p=>({...p,date_rdv:e.target.value}))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">SI</label>
                <input value={scForm.si} onChange={e => setScForm(p=>({...p,si:e.target.value}))}
                  placeholder="# SI..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Km ($)</label>
                <input type="number" step="0.01" value={scForm.km||""} onChange={e => setScForm(p=>({...p,km:parseFloat(e.target.value)||0}))}
                  placeholder="0.00"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
              </div>
            </div>

            {/* Services selected */}
            {scForm.services.length > 0 && (
              <div className="mb-4 bg-slate-50 rounded-lg border border-slate-200 divide-y divide-slate-100">
                {scForm.services.map(s => (
                  <div key={s.service_id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-slate-700">{s.service_name}</span>
                    <div className="flex items-center gap-3">
                      <input type="number" min="1" value={s.quantite}
                        onChange={e => setScForm(p => ({...p, services: p.services.map(sv => sv.service_id===s.service_id ? {...sv, quantite:parseInt(e.target.value)||1, total: r2((parseInt(e.target.value)||1)*sv.prix)} : sv)}))}
                        className="w-12 text-center border border-slate-200 rounded px-1 py-0.5 text-xs" />
                      <span className="font-medium text-[#1a237e] w-16 text-right">{fmt(s.total)}</span>
                      <button onClick={() => removeService(s.service_id)} className="text-slate-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between px-3 py-2 font-semibold text-sm">
                  <span>Total</span>
                  <span className="text-[#1a237e]">{fmt(scForm.services.reduce((s,sv)=>s+sv.total,0) + (parseFloat(scForm.km)||0))}</span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setShowPricingSelector(true)} data-testid="add-service-btn"
                className="flex items-center gap-2 border-2 border-dashed border-[#1a237e]/30 text-[#1a237e] px-4 py-2 rounded-lg text-sm font-medium hover:border-[#1a237e]/60 transition-colors">
                <Plus className="w-4 h-4" />
                Ajouter un service (charte de prix)
              </button>
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-slate-600 block mb-1">Notes</label>
              <textarea value={scForm.notes} onChange={e => setScForm(p=>({...p,notes:e.target.value}))}
                rows={2} placeholder="Informations supplémentaires..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30 resize-none" />
            </div>

            <button onClick={submitSC} disabled={submittingSC} data-testid="submit-sc-btn"
              className="flex items-center gap-2 bg-[#1a237e] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#283593] disabled:opacity-50 transition-colors">
              <Send className="w-4 h-4" />
              {submittingSC ? "Soumission..." : "Soumettre l'extra"}
            </button>
          </div>

          {/* Pricing selector modal */}
          {showPricingSelector && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">Charte de prix — Sélectionner un service</h3>
                  <button onClick={() => setShowPricingSelector(false)} data-testid="close-pricing-selector-btn"
                    className="p-1.5 hover:bg-slate-100 rounded transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                  {pricingChart.length === 0 ? (
                    <p className="p-6 text-slate-400 text-sm text-center">Charte de prix vide</p>
                  ) : pricingChart.map(item => (
                    <PricingItemRow key={item.id} item={item} onAdd={addService} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: RESUME */}
      {tab === "resume" && (
        <div className="space-y-4">
          {!resume ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a237e]" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard label="CA Installateur" value={fmt(resume.total_installateur_brut)} color="blue" sub={`${Math.round(taux*100)}%`} />
                <StatCard label="Total déductions" value={fmt(resume.total_deductions)} color="red" />
                <StatCard label="Net à recevoir" value={fmt(resume.total_installateur_net)} color="green" sub="avant taxes" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <StatCard label="TPS (5%)" value={fmt(resume.tps_total)} color="gray" />
                <StatCard label="TVQ (9.975%)" value={fmt(resume.tvq_total)} color="gray" />
                <StatCard label="Total avec taxes" value={fmt(resume.total_avec_taxes)} color="green" bold />
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                <h3 className="font-semibold text-slate-900 mb-4">{isInstallateur ? 'Ma part par semaine' : 'CA par semaine'} — {selectedYear}</h3>
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {Array.from({length:52},(_,i)=>i+1).map(w => {
                    const wd = resume.weekly?.find(x=>x.semaine===w);
                    const valueKey = isInstallateur ? 'total_installateur_brut' : 'total_contrats';
                    const { start } = getWeekDates(selectedYear, w);
                    const maxVal = Math.max(...(resume.weekly?.map(x=>x[valueKey] || x.total_contrats)||[1]));
                    const val = wd ? (wd[valueKey] || wd.total_contrats || 0) : 0;
                    return (
                      <div key={w} className="flex items-center gap-2 text-xs">
                        <span className="w-14 text-right text-slate-500">Sem. {w}</span>
                        <span className="w-24 text-slate-400">{formatShort(start)}</span>
                        <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                          {val > 0 && (
                            <div className="h-full bg-[#1a237e] rounded transition-all"
                              style={{width:`${Math.max(2,(val/maxVal)*100)}%`}} />
                          )}
                        </div>
                        <span className="w-20 text-right font-medium text-slate-700">
                          {wd ? fmt(val) : "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB: FACTURATION */}
      {tab === "facturation" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-slate-900">Facturation mensuelle SC + Km</h3>
                <p className="text-sm text-slate-500 mt-0.5">Accumulation des service calls et kilométrage à facturer au Marché du Store</p>
              </div>
              <select value={factureMois} onChange={e => setFactureMois(parseInt(e.target.value))}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
                {MONTHS_FR.map((m,i) => <option key={i+1} value={i+1}>{m.charAt(0).toUpperCase()+m.slice(1)} {selectedYear}</option>)}
              </select>
            </div>

            {!facture ? (
              <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1a237e]" /></div>
            ) : facture.lignes.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                <p>Aucun service call ou km pour {MONTHS_FR[factureMois-1]} {selectedYear}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-slate-200 mb-4">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="px-3 py-2 text-left">Sem.</th>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">SI</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Service Call</th>
                        <th className="px-3 py-2 text-right">Km</th>
                        <th className="px-3 py-2 text-right font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facture.lignes.map((l,i) => (
                        <tr key={l.id || l._id || i} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-1.5">S.{l.semaine}</td>
                          <td className="px-3 py-1.5">{l.nom_client}</td>
                          <td className="px-3 py-1.5">{l.date_rdv}</td>
                          <td className="px-3 py-1.5">{l.si}</td>
                          <td className="px-3 py-1.5">{l.description}</td>
                          <td className="px-3 py-1.5 text-right">{l.service_call > 0 ? fmt(l.service_call) : "—"}</td>
                          <td className="px-3 py-1.5 text-right">{l.km_montant > 0 ? fmt(l.km_montant) : "—"}</td>
                          <td className="px-3 py-1.5 text-right font-medium">{fmt(l.service_call + l.km_montant)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-red-50 border-t-2 border-red-200 font-semibold">
                        <td colSpan={5} className="px-3 py-2 text-red-800">TOTAL À FACTURER</td>
                        <td className="px-3 py-2 text-right text-red-700">{fmt(facture.total_service_calls)}</td>
                        <td className="px-3 py-2 text-right text-red-700">{fmt(facture.total_km)}</td>
                        <td className="px-3 py-2 text-right text-red-800 text-base">{fmt(facture.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <div className="flex-1">
                    <label className="text-xs text-slate-600 font-medium block mb-1">Envoyer à (courriel)</label>
                    <input type="email" value={sendFactureEmail} onChange={e => setSendFactureEmail(e.target.value)}
                      placeholder="marche@store.com (ou laisser vide pour admin)"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                  </div>
                  <button onClick={sendFacture} disabled={sendingFacture || facture.total === 0}
                    data-testid="send-facture-btn"
                    className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors mt-5">
                    <Mail className="w-4 h-4" />
                    {sendingFacture ? "Envoi..." : "Envoyer la facture"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* TAB: PERFORMANCE — 🧾 Installateurs Phase 4B */}
      {tab === "performance" && <PerformanceOnglet installateurId={id} anneeInitiale={selectedYear} />}
      {tab === "factures" && <MesFacturesOnglet installateurId={id} anneeInitiale={selectedYear} />}

      {/* TAB: BONUS */}
      {tab === "bonus" && (
        <div className="space-y-4">
          {loadingBonus ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#1a237e]" /></div>
          ) : !bonusData ? (
            <div className="text-center py-12 text-slate-400 text-sm">Aucune donnée de bonus</div>
          ) : (
            <>
              {/* ─── CONCOURS 95K (65% only) ─── */}
              {bonusData.taux <= 0.65 && (
                <div className={`rounded-xl border-2 shadow-sm overflow-hidden ${
                  bonusData.color_95k === "green" ? "border-emerald-300" :
                  bonusData.color_95k === "orange" ? "border-orange-300" : "border-red-300"
                }`}>
                  <div className={`px-5 py-4 ${
                    bonusData.color_95k === "green" ? "bg-emerald-50" :
                    bonusData.color_95k === "orange" ? "bg-orange-50" : "bg-red-50"
                  }`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                          <span className="text-xl">✈️</span> Concours Voyage — Objectif 95 000 $
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">Prix : Crédit voyage de 2 500 $ (taxes incluses) · Réservé aux installateurs 65 %</p>
                      </div>
                      <span data-testid="bonus-95k-badge" className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ml-3 ${
                        bonusData.color_95k === "green" ? "bg-emerald-600 text-white" :
                        bonusData.color_95k === "orange" ? "bg-orange-500 text-white" :
                        "bg-red-600 text-white"
                      }`}>
                        {bonusData.color_95k === "green" ? "En bonne voie ✓" :
                         bonusData.color_95k === "orange" ? "Serré — Accélérez !" : "Objectif difficile"}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm font-medium mb-2">
                        <span className="text-slate-800 font-bold">{fmt(bonusData.ca_installateur)}</span>
                        <span className="text-slate-500">Objectif : {fmt(bonusData.target_95k)}</span>
                      </div>
                      <div className="w-full bg-white rounded-full h-7 overflow-hidden border border-white/60 shadow-inner">
                        <div
                          data-testid="bonus-progress-bar"
                          className={`h-full rounded-full transition-all duration-700 flex items-center justify-end pr-3 ${
                            bonusData.color_95k === "green" ? "bg-emerald-500" :
                            bonusData.color_95k === "orange" ? "bg-orange-400" : "bg-red-500"
                          }`}
                          style={{width: `${Math.min(100, Math.max(2, (bonusData.ca_installateur / bonusData.target_95k) * 100))}%`}}
                        >
                          {(bonusData.ca_installateur / bonusData.target_95k) > 0.12 && (
                            <span className="text-white text-xs font-bold drop-shadow">
                              {Math.round((bonusData.ca_installateur / bonusData.target_95k) * 100)} %
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={`text-sm font-medium ${
                      bonusData.color_95k === "green" ? "text-emerald-700" :
                      bonusData.color_95k === "orange" ? "text-orange-700" : "text-red-700"
                    }`}>
                      {bonusData.color_95k === "green"
                        ? `À votre rythme actuel, vous projettez ${fmt(bonusData.projected_ca)} — Objectif atteint ! 🎉`
                        : `À votre rythme actuel, vous projettez ${fmt(bonusData.projected_ca)} — Manque ${fmt(bonusData.target_95k - bonusData.projected_ca)}`
                      }
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5">
                      Basé sur {bonusData.weeks_with_data} semaine{bonusData.weeks_with_data !== 1 ? "s" : ""} travaillée{bonusData.weeks_with_data !== 1 ? "s" : ""} · Projection sur 50 semaines
                    </p>
                  </div>
                </div>
              )}

              {/* ─── CONCOURS MEILLEURE SEMAINE ─── */}
              <div className="bg-white rounded-xl border-2 border-[#1a237e]/20 shadow-sm p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                      <span className="text-xl">🏨</span> Concours Meilleure Semaine
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5">Prix : Fin de semaine pour 2 à l'hôtel — 750 $ (taxes incluses) · Ouvert à tous les installateurs</p>
                  </div>
                  {bonusData.best_overall_is_me && bonusData.my_best_week_val > 0 && (
                    <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#1a237e] text-white whitespace-nowrap ml-3">
                      🏆 Vous menez !
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* My best */}
                  <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                    <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-2">Votre meilleure semaine</p>
                    {bonusData.my_best_week_val > 0 ? (
                      <>
                        <p className="text-3xl font-bold text-[#1a237e]">{fmt(bonusData.my_best_week_val)}</p>
                        <p className="text-sm text-slate-500 mt-1">Semaine {bonusData.my_best_week_num} · CA brut (100 %)</p>
                      </>
                    ) : (
                      <p className="text-slate-400 text-sm py-2">Aucun rapport cette année</p>
                    )}
                  </div>

                  {/* To beat */}
                  <div className={`rounded-xl border p-4 ${
                    bonusData.best_overall_is_me && bonusData.best_overall_val > 0
                      ? "bg-emerald-50 border-emerald-200"
                      : "bg-amber-50 border-amber-200"
                  }`}>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                      bonusData.best_overall_is_me && bonusData.best_overall_val > 0 ? "text-emerald-600" : "text-amber-600"
                    }`}>
                      {bonusData.best_overall_is_me && bonusData.best_overall_val > 0 ? "Vous menez le classement 🏆" : "Semaine à battre"}
                    </p>
                    {bonusData.best_overall_val > 0 ? (
                      <>
                        <p data-testid="best-overall-val" className={`text-3xl font-bold ${
                          bonusData.best_overall_is_me ? "text-emerald-700" : "text-amber-700"
                        }`}>{fmt(bonusData.best_overall_val)}</p>
                        <p className="text-sm text-slate-600 mt-1 font-medium">{bonusData.best_overall_name}</p>
                        <p className="text-xs text-slate-400">Semaine {bonusData.best_overall_semaine} · CA brut (100 %)</p>
                        {!bonusData.best_overall_is_me && bonusData.my_best_week_val > 0 && (
                          <p className="text-xs text-amber-600 mt-1.5 font-semibold">
                            Manque : {fmt(bonusData.best_overall_val - bonusData.my_best_week_val)}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-400 text-sm py-2">Aucun rapport cette année</p>
                    )}
                  </div>
                </div>
              </div>

              {/* ─── BONUS RÉFÉRENCES ─── */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <span className="text-lg">💰</span> Bonus Références — Peinture
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">100 $ par contrat de peinture signé grâce à votre référence</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-emerald-600 text-lg">{fmt(bonusData.total_references)}</span>
                    {peutAjouterBonus && (
                    <button onClick={() => setShowAddRef(true)} data-testid="add-ref-btn"
                      className="flex items-center gap-1.5 text-xs bg-[#1a237e] text-white px-3 py-1.5 rounded-lg font-medium hover:bg-[#283593] transition-colors">
                      <Plus className="w-3 h-3" />
                      Ajouter
                    </button>
                    )}
                  </div>
                </div>

                {bonusData.references.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">
                    Aucun bonus référence enregistré
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {bonusData.references.map(ref => (
                      <div key={ref.id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <span className="font-medium text-slate-900 text-sm">{ref.client_nom}</span>
                          {ref.date && <span className="text-xs text-slate-400 ml-2">{ref.date}</span>}
                          {ref.notes && <p className="text-xs text-slate-500 mt-0.5 italic">{ref.notes}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-emerald-600">{fmt(ref.montant || 100)}</span>
                          <button onClick={() => deleteBonusRef(ref.id)} className="text-slate-300 hover:text-red-400 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Add Reference Modal */}
          {showAddRef && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
                <div className="flex items-center justify-between p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">Ajouter un bonus référence</h3>
                  <button onClick={() => setShowAddRef(false)} className="p-1.5 hover:bg-slate-100 rounded transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Nom du client *</label>
                    <input value={refForm.client_nom} onChange={e => setRefForm(p => ({...p, client_nom: e.target.value}))}
                      placeholder="Nom du client référé..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Date</label>
                    <input type="date" value={refForm.date} onChange={e => setRefForm(p => ({...p, date: e.target.value}))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Notes</label>
                    <input value={refForm.notes} onChange={e => setRefForm(p => ({...p, notes: e.target.value}))}
                      placeholder="Informations supplémentaires..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                  </div>
                </div>
                <div className="flex gap-3 p-4 border-t border-slate-100">
                  <button onClick={() => setShowAddRef(false)}
                    className="flex-1 border border-slate-200 rounded-lg py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                    Annuler
                  </button>
                  <button onClick={addBonusRef}
                    data-testid="add-bonus-ref-submit-btn"
                    className="flex-1 bg-[#1a237e] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#283593] transition-colors">
                    Ajouter (100 $)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ========== HELPERS ==========
function StatCard({ label, value, color, sub, bold }) {
  const colors = {
    blue: "text-[#1a237e]", red: "text-red-600",
    green: "text-emerald-600", gray: "text-slate-600"
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`${bold ? "text-xl" : "text-lg"} font-bold mt-1 ${colors[color]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function DeductionRow({ data, saving, onSave }) {
  const [montant, setMontant] = useState(data.montant || 0);
  const [desc, setDesc] = useState(data.description || "");
  const [dirty, setDirty] = useState(false);

  function handleChange(val, type) {
    if (type === "montant") { setMontant(val); setDirty(true); }
    else { setDesc(val); setDirty(true); }
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
      <span className="w-24 text-sm font-medium text-slate-700 capitalize">{MONTHS_FR[data.mois-1]}</span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-400">$</span>
        <input type="number" step="0.01" min="0" value={montant===0?"":montant}
          onChange={e => handleChange(parseFloat(e.target.value)||0,"montant")}
          placeholder="0.00"
          className="w-24 border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
      </div>
      <input type="text" value={desc} onChange={e => handleChange(e.target.value,"desc")}
        placeholder="Description (assurance, outils, etc.)"
        className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
      {dirty && (
        <button onClick={() => { onSave(montant, desc); setDirty(false); }} disabled={saving}
          className="text-xs bg-[#1a237e] text-white px-3 py-1.5 rounded font-medium hover:bg-[#283593] disabled:opacity-50 transition-colors">
          {saving ? "..." : "Sauv."}
        </button>
      )}
    </div>
  );
}
